/**
 * Credential shapes, sort key encoding and the internal sentinel namespace.
 *
 * Source of truth: build plan sections 5.0 (the two rules that govern every
 * key), 5.1.1 (the write key item), 6.3 (SDK storage scoping) and 7.5.
 */
import { MIN_ALLOWED_CODEPOINT, STORAGE_SCOPE_CHARS } from './limits.js';
/* ------------------------------------------------------------------------- *
 * Credential shapes.
 * ------------------------------------------------------------------------- */
/**
 * A write key: `zs_`, one environment letter, `_`, then 26 base62 characters.
 * Matched by regex before any database call, so an unknown shaped key costs a
 * 401 and nothing else.
 */
export const WRITE_KEY_RE = /^zs_[psd]_[0-9A-Za-z]{26}$/;
/** A personal read token. Shown once, stored as a SHA-256 digest. */
export const READ_TOKEN_RE = /^zsk_live_[A-Za-z0-9_-]{43}$/;
/**
 * The non secret write key prefix persisted on the key listing item and used as
 * the path segment of `GET /v1/config/<key_prefix>`. `zs_p_` plus the first six
 * characters of the random body, for example `zs_p_7Kq2mB`.
 */
export const KEY_PREFIX_CHARS = 11;
/** Environment letters a write key may carry, in `zs_<letter>_...`. */
export const WRITE_KEY_ENV_LETTERS = ['p', 's', 'd'];
/** The three environments, spelled as the SDK and the read API spell them. */
export const ENVIRONMENTS = ['production', 'staging', 'development'];
/** Write key environment letter to environment name. */
export const ENVIRONMENT_BY_KEY_LETTER = Object.freeze({
    p: 'production',
    s: 'staging',
    d: 'development',
});
/** True when `value` is a syntactically valid write key. */
export function isWriteKey(value) {
    return typeof value === 'string' && WRITE_KEY_RE.test(value);
}
/** True when `value` is a syntactically valid personal read token. */
export function isReadToken(value) {
    return typeof value === 'string' && READ_TOKEN_RE.test(value);
}
/**
 * The non secret prefix of a write key, or null when the key is malformed.
 * Never derive this from an unvalidated string: a malformed key must not
 * produce a prefix that could be logged as if it were real.
 */
export function writeKeyPrefix(writeKey) {
    if (!WRITE_KEY_RE.test(writeKey))
        return null;
    return writeKey.slice(0, KEY_PREFIX_CHARS);
}
/** The environment a write key belongs to, or null when it is malformed. */
export function writeKeyEnvironment(writeKey) {
    if (!WRITE_KEY_RE.test(writeKey))
        return null;
    const letter = writeKey.charAt(3);
    return ENVIRONMENT_BY_KEY_LETTER[letter];
}
/**
 * The suffix that scopes SDK storage keys (`zs_aid_`, `zs_sid_`, `zs_q_`) to
 * one write key, so two ZeliSight projects on one page never collide. Returns
 * null for a malformed key.
 */
export function storageScope(writeKey) {
    if (!WRITE_KEY_RE.test(writeKey))
        return null;
    return writeKey.slice(0, STORAGE_SCOPE_CHARS);
}
/* ------------------------------------------------------------------------- *
 * Rule 1: encode before you concatenate.
 * ------------------------------------------------------------------------- */
/**
 * Percent-encode the two characters that are structural in our sort keys.
 *
 * Applied to every customer supplied segment before it enters a sort key. A
 * customer sending `checkout#step2` would otherwise produce a `#` delimited
 * sort key that parses back to a different tuple and silently merges or splits
 * their own metrics.
 *
 * `%` must be encoded first, otherwise `dec(enc(x))` is not the identity.
 */
export function enc(segment) {
    return segment.replace(/%/g, '%25').replace(/#/g, '%23');
}
/** Inverse of {@link enc}. `%23` must be decoded first. */
export function dec(segment) {
    return segment.replace(/%23/g, '#').replace(/%25/g, '%');
}
/* ------------------------------------------------------------------------- *
 * Rule 2: sentinels live in a namespace the customer cannot reach.
 * ------------------------------------------------------------------------- */
/**
 * Prefix of every internal series segment. A literal NUL byte. The ingest
 * validator rejects any event name or property key containing a codepoint below
 * MIN_ALLOWED_CODEPOINT, which makes this namespace unreachable from customer
 * input by construction rather than by convention.
 */
export const SENTINEL_PREFIX = '\u0000';
/** All events in the bucket. */
export const SENTINEL_ALL = `${SENTINEL_PREFIX}all`;
/** Session count in the bucket. */
export const SENTINEL_SESSIONS = `${SENTINEL_PREFIX}sess`;
/** Overflow bucket once a project passes MAX_EVENT_NAMES_PER_PROJECT names. */
export const SENTINEL_OTHER = `${SENTINEL_PREFIX}other`;
/** Replacement for a suppressed high cardinality property value. */
export const SENTINEL_HIGH_CARDINALITY = `${SENTINEL_PREFIX}hicard`;
/** Heatmap events (ADR 0034), counted apart from product events so they never
 *  reach a tile but still reconcile against the lake. */
export const SENTINEL_HEATMAP = `${SENTINEL_PREFIX}heatmap`;
/** Every sentinel segment, for exhaustive tests and for the Python mirror. */
export const SENTINELS = [
    SENTINEL_ALL,
    SENTINEL_SESSIONS,
    SENTINEL_OTHER,
    SENTINEL_HIGH_CARDINALITY,
    SENTINEL_HEATMAP,
];
/**
 * Prefixes reserved by ZeliSight in the property and event name namespace.
 *
 * `$` marks SDK and platform owned names such as `$pageview` and
 * `$queue_overflow`. The NUL prefix marks the internal series namespace and is
 * unreachable from customer input.
 *
 * A `$` prefixed name from a customer is accepted, never rejected: it is
 * ordinary data that simply shares a namespace with ours, and rejecting it
 * would be a silent data loss for a migrating PostHog customer.
 */
export const RESERVED_PROPERTY_PREFIXES = ['$', SENTINEL_PREFIX];
/** True when `name` sits in a ZeliSight reserved namespace. */
export function isReservedName(name) {
    return RESERVED_PROPERTY_PREFIXES.some((prefix) => name.startsWith(prefix));
}
/* ------------------------------------------------------------------------- *
 * Charset guard shared by event names, property keys and distinct ids.
 * ------------------------------------------------------------------------- */
/**
 * True when `value` contains a codepoint below MIN_ALLOWED_CODEPOINT. Such a
 * value must never reach a sort key: it is how the sentinel namespace would
 * become reachable.
 */
export function hasControlCodepoint(value) {
    for (const char of value) {
        const codepoint = char.codePointAt(0);
        if (codepoint !== undefined && codepoint < MIN_ALLOWED_CODEPOINT) {
            return true;
        }
    }
    return false;
}
/**
 * Length of `value` in Unicode codepoints, not UTF-16 code units.
 *
 * Every length limit in this contract is expressed in codepoints so that the
 * TypeScript and the Python validator agree exactly. `'\u{1F600}'.length` is 2
 * in JavaScript and 1 in Python, and a limit that disagrees across the two
 * implementations is a limit that silently truncates on one side only.
 */
export function codepointLength(value) {
    let count = 0;
    for (let index = 0; index < value.length;) {
        const codepoint = value.codePointAt(index);
        index += codepoint !== undefined && codepoint > 0xffff ? 2 : 1;
        count += 1;
    }
    return count;
}
/** `value` cut to at most `maxCodepoints` codepoints, never mid surrogate pair. */
export function truncateCodepoints(value, maxCodepoints) {
    if (maxCodepoints <= 0)
        return '';
    let count = 0;
    for (let index = 0; index < value.length;) {
        if (count === maxCodepoints)
            return value.slice(0, index);
        const codepoint = value.codePointAt(index);
        index += codepoint !== undefined && codepoint > 0xffff ? 2 : 1;
        count += 1;
    }
    return value;
}
//# sourceMappingURL=keys.js.map